import get from "lodash-es/get";
import { type FieldValues } from "react-hook-form";
import z from "zod";

function getNextSchema(
  formSchema: z.ZodSchema | undefined,
  values: FieldValues,
  name: string,
): z.ZodSchema | undefined {
  if (formSchema instanceof z.ZodObject) {
    return get(formSchema.shape, name);
  }

  if (formSchema instanceof z.ZodArray) {
    return formSchema._def.type;
  }

  if (formSchema instanceof z.ZodDiscriminatedUnion) {
    const discriminator = formSchema._def.discriminator;

    if (name === discriminator) {
      return z.string();
    }

    const discriminatorValue = values[discriminator];
    const optionSchema = formSchema._def.optionsMap.get(discriminatorValue);

    return getNextSchema(optionSchema, values, name);
  }

  if (formSchema instanceof z.ZodEffects) {
    return getNextSchema(formSchema._def.schema, values, name);
  }

  if (
    formSchema instanceof z.ZodOptional ||
    formSchema instanceof z.ZodNullable ||
    formSchema instanceof z.ZodDefault ||
    formSchema instanceof z.ZodCatch
  ) {
    return getNextSchema(formSchema._def.innerType, values, name);
  }

  return undefined;
}

type IsFieldRequiredParams = {
  formSchema: z.ZodSchema;
  values: FieldValues;
  path: string;
};

function getFieldSchema({ formSchema, values, path }: IsFieldRequiredParams) {
  const parts = (!Array.isArray(path) ? path.split(".") : path).filter(Boolean);

  let currentSchema: z.ZodSchema | undefined = formSchema;
  let currentValues = values;

  for (const [index, name] of parts.entries()) {
    if (!isNaN(Number(name))) {
      if (currentSchema instanceof z.ZodArray) {
        currentSchema = currentSchema._def.type;
      }

      currentValues = get(currentValues, name, {});
      continue;
    }

    const nextSchema = getNextSchema(currentSchema, currentValues, name);

    if (index === parts.length - 1) {
      return nextSchema;
    }

    currentSchema = nextSchema;
    currentValues = get(currentValues, name, {});
  }

  return currentSchema;
}

export function isFieldRequired(params: {
  formSchema: z.ZodSchema;
  values: FieldValues;
  path: string;
}) {
  const schema = getFieldSchema(params);

  if (!schema) {
    return false;
  }

  return !(
    schema instanceof z.ZodOptional ||
    schema instanceof z.ZodNullable ||
    schema instanceof z.ZodBoolean
  );
}
